import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";

import { TemplateTokenNodeView } from "./TemplateTokenNodeView";

export const TEMPLATE_TOKEN_NODE_NAME = "templateToken";
/** Attribute the token's exact raw string is stored under, both in the doc and serialized HTML. */
export const TEMPLATE_TOKEN_ATTR = "data-template-token";

/**
 * Inline atom node representing a single templated token (`{{…}}`, `{url}[text]`, or a legacy
 * `{name}`/`{sender}`). It carries only the exact `raw` string; the visible label, kind, and
 * styling are derived from `raw` at render time (see {@link TemplateTokenNodeView}) so a single
 * source of truth governs both display and serialization.
 *
 * On serialization the node renders as `<span data-template-token="<raw>"></span>`; the body
 * serializer then collapses that span back to the bare `raw` string so the stored `html`/`message`
 * contain the literal token the backend resolver expects (see `serialization.ts`).
 */
export const TemplateTokenNode = Node.create({
  name: TEMPLATE_TOKEN_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes: () => ({
    raw: {
      default: "",
      parseHTML: (element: HTMLElement) => element.getAttribute(TEMPLATE_TOKEN_ATTR) ?? "",
      renderHTML: (attributes: { raw?: string }) => ({ [TEMPLATE_TOKEN_ATTR]: attributes.raw ?? "" }),
    },
  }),

  parseHTML: () => [{ tag: `span[${TEMPLATE_TOKEN_ATTR}]` }],

  renderHTML: ({ HTMLAttributes }) => ["span", mergeAttributes(HTMLAttributes)],

  addNodeView: () => ReactNodeViewRenderer(TemplateTokenNodeView),
});
